import { useState, useContext } from 'react';
import MedicalInfoDrawer from '../../CampaignDetail/drawers/MedicalInfoDrawer';
import { MdOutlineCheckCircle } from 'react-icons/md';
import { LuCircleEllipsis } from 'react-icons/lu';
import { AiOutlineExclamationCircle } from 'react-icons/ai';
import { CampaignContext } from '~/context/CampaignProvider';

interface Props {
    patient?: any;
    campaign?: any;
    campaignRequest?: any;
    metadata?: any;
    application?: any;
    completionStatus?: any;
    onDataSaved?: () => void;
}

const IDENTITY_CHOICES = [
    { value: 1, label: 'Client' },
    { value: 2, label: 'Spouse' },
    { value: 3, label: 'Parent of minor client' },
    { value: 4, label: 'Legal Guardian' },
    { value: 5, label: 'POA' },
];

const MedicalInfoTab: React.FC<Props> = ({
    metadata,
    patient,
    campaign,
    campaignRequest,
    application,
    completionStatus,
    onDataSaved,
}) => {
    const contextValue = useContext(CampaignContext);
    if (!contextValue) throw new Error('CampaignContext not found');

    const [state, campaignDispatch] = contextValue as unknown as [
        { campaignData: any },
        any
    ];
    const campaignData = state.campaignData;

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const getIdentityLabel = (value: number | null | undefined) => {
        if (!value) return '';
        const choice = IDENTITY_CHOICES.find((c) => c.value === value);
        return choice?.label || '';
    };

    // Use backend completion status if available, fallback to local check
    const medproSection = completionStatus?.sections?.find(
        (s: any) => s.key === 'medpro'
    );
    const isMedproComplete =
        medproSection?.is_complete ??
        (patient?.medical_professional?.medical_center?.location &&
            patient?.medical_professional?.medical_center?.name &&
            patient?.medical_professional?.person &&
            application?.mp_certify_identity &&
            application?.mp_signature);

    // Field name to human-readable label mapping
    const fieldLabels: Record<string, string> = {
        mp_give_consent_for_mp_to_provide_info:
            'Medical Professional consent to provide info',
        mp_certify_identity: 'I certify that I am the',
        mp_signature: 'Name of individual saving these agreements',
        mp_signature_date: 'Signature date',
    };

    // Get missing fields - prefer backend data, but if empty and section is incomplete, check manually
    const getMissingFields = (): string[] => {
        // If backend provided missing fields and they're not empty, use those
        if (
            medproSection?.missing_fields &&
            medproSection.missing_fields.length > 0
        ) {
            return medproSection.missing_fields;
        }

        // If section is incomplete but backend didn't provide fields, manually check
        if (!isMedproComplete) {
            const missing: string[] = [];
            if (!patient?.medical_professional?.medical_center?.location)
                missing.push('medical_center_location');
            if (!patient?.medical_professional?.medical_center?.name)
                missing.push('medical_center_name');
            if (!patient?.medical_professional?.person)
                missing.push('medical_professional');
            if (!application?.mp_certify_identity)
                missing.push('mp_certify_identity');
            if (!application?.mp_signature) missing.push('mp_signature');
            return missing;
        }

        return [];
    };

    const missingFields = getMissingFields();

    return (
        <>
            <div className='mb-7.5 flex items-center justify-between'>
                <div>
                    <h3
                        className={`mb-2 flex items-center text-[16px]/[24px] font-medium uppercase ${
                            isMedproComplete
                                ? 'text-[#AFC12D]'
                                : 'text-[#FEBF3E]'
                        }`}
                    >
                        {isMedproComplete ? (
                            <MdOutlineCheckCircle className='mr-2' size={24} />
                        ) : (
                            <LuCircleEllipsis className='mr-2' size={24} />
                        )}
                        {isMedproComplete ? 'Complete' : 'Pending'}
                    </h3>
                    <h2 className='text-[22px]/[28px] font-bold'>
                        Medical Professional Info
                    </h2>
                </div>

                <button
                    className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                    onClick={() => {
                        setIsDrawerOpen(true);
                    }}
                >
                    Edit
                </button>
            </div>

            {!isMedproComplete && (
                <div className='mb-7.5 rounded-lg border border-[#F02D3A] bg-[#FEF2F2] p-4'>
                    <h3 className='mb-2 flex items-center text-[14px]/[20px] font-semibold text-[#991B1B]'>
                        <AiOutlineExclamationCircle
                            className='mr-2'
                            size={20}
                        />
                        Missing Required Fields
                    </h3>
                    {missingFields.length > 0 ? (
                        <ul className='ml-6 list-disc space-y-1 text-[14px]/[20px] text-[#7F1D1D]'>
                            {missingFields.map((field: string) => (
                                <li key={field}>
                                    {fieldLabels[field] || field}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className='text-[14px]/[20px] text-[#7F1D1D]'>
                            Please complete all required fields in this section.
                        </p>
                    )}
                </div>
            )}

            <div className='mb-7.5'>
                <div className='flex justify-between'>
                    <div className='grow'>
                        <div
                            className={`mb-[16px] ${
                                patient?.medical_professional?.medical_center
                                    ?.location
                                    ? ''
                                    : 'bg-[#FDEAEB] px-1'
                            }`}
                        >
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Where is your Medical Professional located?
                                <span className='align-top text-[#F02D3A]'>
                                    *
                                </span>
                            </label>
                            {patient?.medical_professional?.medical_center
                                ?.location ? (
                                <div>
                                    {
                                        patient?.medical_professional
                                            ?.medical_center?.location
                                    }
                                </div>
                            ) : (
                                <div className='text-danger'>Required</div>
                            )}
                        </div>
                        <div
                            className={`mb-[16px] ${
                                patient?.medical_professional?.medical_center
                                    ?.name
                                    ? ''
                                    : 'bg-[#FDEAEB] px-1'
                            }`}
                        >
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                What is your Medical Professional's medical
                                center?
                                <span className='align-top text-[#F02D3A]'>
                                    *
                                </span>
                            </label>
                            {patient?.medical_professional?.medical_center
                                ?.name ? (
                                <div>
                                    {
                                        patient?.medical_professional
                                            ?.medical_center?.name
                                    }
                                </div>
                            ) : (
                                <div className='text-danger'>Required</div>
                            )}
                        </div>
                        <div
                            className={`mb-[16px] ${
                                patient?.medical_professional?.person
                                    ? ''
                                    : 'bg-[#FDEAEB] px-1'
                            }`}
                        >
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Who is your Medical Professional?
                                <span className='align-top text-[#F02D3A]'>
                                    *
                                </span>
                            </label>
                            {patient?.medical_professional?.person ? (
                                <div>
                                    {
                                        patient?.medical_professional?.person
                                            ?.first_name
                                    }{' '}
                                    {
                                        patient?.medical_professional?.person
                                            ?.last_name
                                    }
                                </div>
                            ) : (
                                <div className='text-danger'>Required</div>
                            )}
                        </div>
                        <div
                            className={`mb-[16px] ${
                                application?.mp_certify_identity
                                    ? ''
                                    : 'bg-[#FDEAEB] px-1'
                            }`}
                        >
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                I certify that I am the:
                                <span className='align-top text-[#F02D3A]'>
                                    *
                                </span>
                            </label>
                            {application?.mp_certify_identity ? (
                                <div>
                                    {getIdentityLabel(
                                        application.mp_certify_identity
                                    )}
                                </div>
                            ) : (
                                <div className='text-danger'>Required</div>
                            )}
                        </div>
                        <div
                            className={`mb-[16px] ${
                                application?.mp_signature
                                    ? ''
                                    : 'bg-[#FDEAEB] px-1'
                            }`}
                        >
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Name of individual saving these agreements
                                <span className='align-top text-[#F02D3A]'>
                                    *
                                </span>
                            </label>
                            {application?.mp_signature ? (
                                <div>{application.mp_signature}</div>
                            ) : (
                                <div className='text-danger'>Required</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <MedicalInfoDrawer
                isOpen={isDrawerOpen}
                setIsOpen={setIsDrawerOpen}
                patient={patient}
                application={application}
                applicationId={application?.id} // Use application ID for Campaign Application Detail
                onUpdate={(response) => {
                    console.log('Medical professional updated:', response);
                    // The response includes complete campaign data, update context directly
                    const {
                        campaign: updatedCampaign,
                        campaign_id,
                        patient: updatedPatient,
                        csc: updatedCsc,
                        campaign_request: updatedCampaignRequest,
                        notes: updatedNotes,
                        coordinator: updatedCoordinator,
                        campaign_approval: updatedCampaignApproval,
                        financial_coordinator: updatedFinancialCoordinator,
                        ...applicationFields
                    } = response;

                    // Add campaign ID back to application object
                    const updatedApplication = {
                        ...applicationFields,
                        campaign: campaign_id,
                    };

                    // Update the context with the new data
                    campaignDispatch({
                        type: 'set_campaign',
                        payload: {
                            campaign: updatedCampaign,
                            patient: updatedPatient,
                            csc: updatedCsc,
                            campaign_request: updatedCampaignRequest,
                            notes: updatedNotes,
                            campaignPhotoInfo:
                                campaignData?.campaignPhotoInfo || null,
                            coordinator: updatedCoordinator,
                            financial_coordinator:
                                updatedFinancialCoordinator ||
                                campaignData?.financial_coordinator ||
                                null,
                            count: campaignData?.count || 0,
                            application: updatedApplication,
                            campaign_approval: updatedCampaignApproval,
                        },
                    });

                    // Refresh completion status
                    if (onDataSaved) {
                        onDataSaved();
                    }
                }}
            />
        </>
    );
};

export default MedicalInfoTab;
